import Box from "@mui/material/Box";
import * as React from "react";
import {TrackToggle, useTrackToggle} from "@livekit/components-react";
import {RoomEvent, Track} from "livekit-client";
import {NewMediaDeviceMenu} from "./NewMediaDeviceMenu";
import {forwardRef, useEffect, useImperativeHandle, useState, useRef} from "react";
import {collection, doc, onSnapshot, query, updateDoc, where} from "firebase/firestore";
import {db} from "../../Firebase";
import {kRoomStateCollectionName, RoomName} from "../../utils/CollectionConstants";
import {DropdownArrowIcon} from "./SettingsIcon";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import {BackgroundMenu} from "./BackgroundMenu";
import {Typography} from "@mui/material";
import {useCustomTrackToggle} from "./useCustomTrackToggle";
import {MusicVideo} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import CheckIcon from "@mui/icons-material/Check";
import CallEndIcon from "@mui/icons-material/CallEnd";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { releasePlaceholderResources } from "./livekitPlaceholderTracks";

const RecordVideoShowVideo = 0;
const ShowVideoRecordAudio = 1;
const NoVideoRecordAudio = 2;

const textStyle = {
    fontFamily: "Inter",
    fontSize: {md: "9px", lg: "11px", xl: "14px"},
    fontStyle: "normal",
    fontWeight: {md: "350", lg: "400", xl: "500"},
    lineHeight: {md: "13px", lg: "15px", xl: "20px"},
    letterSpacing: {md: "0.1px", lg: "0.1px", xl: "0.14px"},
    color: "var(--app-text-primary)",
    // disable highlight on double click
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    KhtmlUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
}

const dropdownArrowStyle = {
    display: "flex",
    padding: {md: "7px 8px", lg: "11px 9px", xl: "14px 12px"},
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "var(--app-bg-subtle)",
    width: {md: "32px", lg: "36px", xl: "48px"},
    height: {md: "34px", lg: "39px", xl: "52px"},
    alignSelf: "stretch",
    cursor: "pointer",
    // 4px border radius for the top right and bottom right corners
    borderRadius: "0 5px 5px 0",
}

const MicrophoneIcon = ({color = "var(--app-text-primary)"}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 21 20" fill="none">
            <path
                d="M10.5 0.833313C9.83696 0.833313 9.20107 1.09671 8.73223 1.56555C8.26339 2.03439 8 2.67027 8 3.33331V9.99998C8 10.663 8.26339 11.2989 8.73223 11.7677C9.20107 12.2366 9.83696 12.5 10.5 12.5C11.163 12.5 11.7989 12.2366 12.2678 11.7677C12.7366 11.2989 13 10.663 13 9.99998V3.33331C13 2.67027 12.7366 2.03439 12.2678 1.56555C11.7989 1.09671 11.163 0.833313 10.5 0.833313V0.833313Z"
                stroke={color} strokeWidth="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path
                d="M16.3332 8.33331V9.99998C16.3332 11.5471 15.7186 13.0308 14.6246 14.1248C13.5307 15.2187 12.0469 15.8333 10.4998 15.8333C8.95274 15.8333 7.46901 15.2187 6.37505 14.1248C5.28109 13.0308 4.6665 11.5471 4.6665 9.99998V8.33331"
                stroke={color} strokeWidth="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.5 15.8333V19.1666" stroke={color} strokeWidth="1.5" stroke-linecap="round"
                  stroke-linejoin="round"/>
            <path d="M7.1665 19.1667H13.8332" stroke={color} strokeWidth="1.5" stroke-linecap="round"
                  stroke-linejoin="round"/>
        </svg>
    )
}

const MicDisabledIcon = ({color = "var(--app-text-primary)"}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 21 20" fill="none">
            <path
                d="M10.5 0.833313C9.83696 0.833313 9.20107 1.09671 8.73223 1.56555C8.26339 2.03439 8 2.67027 8 3.33331V9.99998C8 10.663 8.26339 11.2989 8.73223 11.7677C9.20107 12.2366 9.83696 12.5 10.5 12.5C11.163 12.5 11.7989 12.2366 12.2678 11.7677C12.7366 11.2989 13 10.663 13 9.99998V3.33331C13 2.67027 12.7366 2.03439 12.2678 1.56555C11.7989 1.09671 11.163 0.833313 10.5 0.833313V0.833313Z"
                stroke={color} strokeWidth="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path
                d="M16.3332 8.33331V9.99998C16.3332 11.5471 15.7186 13.0308 14.6246 14.1248C13.5307 15.2187 12.0469 15.8333 10.4998 15.8333C8.95274 15.8333 7.46901 15.2187 6.37505 14.1248C5.28109 13.0308 4.6665 11.5471 4.6665 9.99998V8.33331"
                stroke={color} strokeWidth="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.5 15.8333V19.1666" stroke={color} strokeWidth="1.5" stroke-linecap="round"
                  stroke-linejoin="round"/>
            <path d="M7.1665 19.1667H13.8332" stroke={color} strokeWidth="1.5" stroke-linecap="round"
                  stroke-linejoin="round"/>
            <path d="M3 3L18 18" stroke={color} strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    );
}

const CameraIcon = ({color = "var(--app-text-primary)"}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 21 20" fill="none">
            <path
                d="M4.25 3.75C2.86929 3.75 1.75 4.86929 1.75 6.25V13.75C1.75 15.1307 2.86929 16.25 4.25 16.25H11.125C12.5057 16.25 13.625 15.1307 13.625 13.75V6.25C13.625 4.86929 12.5057 3.75 11.125 3.75H4.25Z"
                stroke={color} strokeWidth="1.5"/>
            <path
                d="M16.1161 15.625L13.875 13.3839V6.61609L16.1161 4.37497C16.9036 3.58751 18.25 4.14523 18.25 5.25885V14.7411C18.25 15.8547 16.9036 16.4124 16.1161 15.625Z"
                stroke={color} strokeWidth="1.5"/>
        </svg>
    )
}

const CamDisabledIcon = ({color = "var(--app-text-primary)"}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 21 20" fill="none">
            <path
                d="M4.25 3.75C2.86929 3.75 1.75 4.86929 1.75 6.25V13.75C1.75 15.1307 2.86929 16.25 4.25 16.25H11.125C12.5057 16.25 13.625 15.1307 13.625 13.75V6.25C13.625 4.86929 12.5057 3.75 11.125 3.75H4.25Z"
                stroke={color} strokeWidth="1.5"/>
            <path
                d="M16.1161 15.625L13.875 13.3839V6.61609L16.1161 4.37497C16.9036 3.58751 18.25 4.14523 18.25 5.25885V14.7411C18.25 15.8547 16.9036 16.4124 16.1161 15.625Z"
                stroke={color} strokeWidth="1.5"/>
            <path d="M3 3L18 18" stroke={color} strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    )
}

const ShareScreenIcon = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
            <path d="M6.5 9L6.5 3" stroke="var(--app-text-primary)" strokeWidth="1.28571" stroke-linecap="round"
                  stroke-linejoin="round"/>
            <path d="M3.5 6L6.5 3L9.5 6" stroke="var(--app-text-primary)" strokeWidth="1.28571" stroke-linecap="round"
                  stroke-linejoin="round"/>
        </svg>
    );
};

const ActiveShareScreenIcon = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
            <path d="M6.5 9L6.5 3" stroke="#2B6BFD" strokeWidth="1.28571" stroke-linecap="round"
                  stroke-linejoin="round"/>
            <path d="M3.5 6L6.5 3L9.5 6" stroke="#2B6BFD" strokeWidth="1.28571" stroke-linecap="round"
                  stroke-linejoin="round"/>
        </svg>
    )
}

export const RecordingModeButton = ({roomName}) => {

    const buttonRef = React.useRef(null);

    const [recordingModeAudio, setRecordingModeAudio] = useState(false);

    const IsRecordingModeAudio = (recordingType) => {
        return recordingType === 1 || recordingType === 2;
    }

    useEffect(() => {
        if (roomName === '') {
            return;
        }

        const q = query(collection(db, kRoomStateCollectionName), where(RoomName, "==", roomName));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const newRoomState = [];
            querySnapshot.forEach((doc) => {
                newRoomState.push(doc.data());
            });
            if (newRoomState.length > 0) {
                setRecordingModeAudio(IsRecordingModeAudio(newRoomState[0].recordingType));
            }
        });

        return () => unsubscribe();
    }, [roomName, setRecordingModeAudio]);

    const updateRecordingMode = async (audioRecording) => {
        if (roomName === '') {
            return;
        }

        const recordingType = audioRecording ? NoVideoRecordAudio : RecordVideoShowVideo;

        const entityRef = doc(db, kRoomStateCollectionName, roomName);
        try {
            await updateDoc(entityRef, {recordingType: recordingType});
        } catch (error) {
            console.error('Error updating recording type for room: ', error);
        }
    }

    const buttonStyle = {
        display: "flex",
        padding: {md: "8px 11px", lg: "12px 18px", xl: "16px 24px"},
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        backgroundColor: "var(--app-bg-subtle)",
        width: {md: "115px", lg: "179px", xl: "179px"},
        height: {md: "34px", lg: "39px", xl: "52px"},
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: 'var(--app-border-subtle)',
        },
        borderRadius: "5px 0 0 5px",
    }

    return (
        <Box flexDirection="row" display="flex">
            <Box onClick={() => {
            }} sx={buttonStyle} alignItems="center" justifyContent="center" flexDirection="row">
                {recordingModeAudio ? <MicrophoneIcon/> : <CameraIcon/>}
                <div sx={textStyle}>
                    {recordingModeAudio ? "Audio-only" : "Video"}
                </div>
            </Box>
            <Box sx={dropdownArrowStyle} ref={buttonRef}>
                <DropdownArrowIcon/>
            </Box>
        </Box>
    )

}

export const CameraButton = ({onChange, saveVideoInputDevice, room}) => {
    const {buttonProps, enabled} = useCustomTrackToggle({
        source: Track.Source.Camera,
        onChange: onChange,
        maybeRoom: room
    });

    const handleClick = () => {
        buttonProps.onClick();
    }

    const buttonStyle = {
        display: "flex",
        padding: {md: "8px 11px", lg: "10px 12px", xl: "12px 18px"},
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        backgroundColor: "var(--app-bg-subtle)",
        width: {md: "106px", lg: "119px", xl: "159px"},
        height: {md: "34px", lg: "39px", xl: "45px"},
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: 'var(--app-border-subtle)',
        },
        borderRadius: "5px 0 0 5px",
    }

    const theme = useTheme();

    const isLg = useMediaQuery(theme.breakpoints.up("lg"));
    const isXl = useMediaQuery(theme.breakpoints.up("xl"));

    const width = isXl ? 159 : isLg ? 119 : 106;

    return (
        <Box flexDirection="row" display="flex">
            <Box onClick={handleClick} sx={buttonStyle} alignItems="center" justifyContent="center" flexDirection="row">
                {enabled ? <CameraIcon/> : <CamDisabledIcon/>}
                <div sx={textStyle}>Camera</div>
            </Box>
            <NewMediaDeviceMenu kind="videoinput" room={room}
                                onActiveDeviceChange={(_kind, deviceId) => saveVideoInputDevice(deviceId ?? '')}
                                width={width}/>
        </Box>
    )
}

export const NewCameraButton = ({onChange, selectDisabled, isButtonDisabled, saveVideoInputDevice, room, initialState}) => {
    const {buttonProps, enabled} = useCustomTrackToggle({
        source: Track.Source.Camera,
        onChange: onChange,
        maybeRoom: room,
        initialState: initialState,
    });

    const handleClick = () => {
        if (isButtonDisabled) {
            return;
        }
        buttonProps.onClick();
    }

    const buttonStyle = {
        display: "flex",
        padding: {md: "4px 4px"},
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: isButtonDisabled ? "not-allowed" : "pointer",
        borderRadius: "5px 0 0 5px",
        border: "1px solid var(--app-border-subtle)",
        backgroundColor: isButtonDisabled ? "var(--app-border-subtle)" : "var(--app-bg-subtle)",
        width: "40px",
        height: "40px",
        opacity: isButtonDisabled ? 0.6 : 1,
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: isButtonDisabled ? 'var(--app-border-subtle)' : 'var(--app-border-subtle)',
        },
    }

    const theme = useTheme();

    const isLg = useMediaQuery(theme.breakpoints.up("lg"));
    const isXl = useMediaQuery(theme.breakpoints.up("xl"));

    const width = isXl ? 159 : isLg ? 119 : 106;

    return (
        <Box flexDirection="row" display="flex">
            <Tooltip title={enabled ? "Disable camera" : "Enable camera"} placement={"bottom"}>
                <Box onClick={handleClick} sx={buttonStyle} alignItems="center" justifyContent="center"
                     flexDirection="row">
                    {enabled ? <CameraIcon/> : <CamDisabledIcon/>}
                </Box>
            </Tooltip>
            <NewMediaDeviceMenu kind="videoinput" room={room} isDisabled={selectDisabled}
                                onActiveDeviceChange={(_kind, deviceId) => saveVideoInputDevice(deviceId ?? '')}
                                width={width} isPrejoin={false}/>
        </Box>
    )
}

export const BackgroundButton = ({
                                     setBackgroundImage, backgroundImageIndex,
                                     setBackgroundImageIndex
                                 }) => {

    return (
        <Box flexDirection="row" display="flex">
            <BackgroundMenu setBackgroundImage={setBackgroundImage} initialSelection="none" onBackgroundChange={() => {
            }} backgroundImageIndex={backgroundImageIndex} setBackgroundImageIndex={setBackgroundImageIndex}/>
        </Box>
    )
}

export const ShareScreenButton = ({onChange, disabled, room}) => {
    const {buttonProps, enabled} = useCustomTrackToggle({
        source: Track.Source.ScreenShare,
        onChange: onChange,
        maybeRoom: room
    });

    const handleClick = () => {
        buttonProps.onClick();
    }

    const buttonStyle = {
        display: "flex",
        padding: {md: "11px 16px", lg: "12px 18px", xl: "12px 18px"},
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        backgroundColor: "var(--app-bg-subtle)",
        width: {md: "112px", lg: "179px", xl: "179px"},
        height: {md: "34px", lg: "39px", xl: "45px"},
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: 'var(--app-border-subtle)',
        },
        // 5px border radius on the top left and bottom left corners
        borderRadius: "5px 5px 5px 5px",
    }

    const enabledButtonStyle = {
        ...buttonStyle,
        backgroundColor: "var(--app-border-subtle)",
        border: "1.5px solid #2B6BFD",
    }

    const effectiveStyle = enabled ? enabledButtonStyle : buttonStyle;

    const shareScreenBoxStyle = {
        width: "20px",
        height: "15px",
        padding: "1px",
        flexShrink: 0,
        borderRadius: "3px",
        border: "1.5px solid var(--app-text-primary)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    }

    const theme = useTheme();

    const isLg = useMediaQuery(theme.breakpoints.up("lg"));

    const buttonText = (enabled) => {
        if (enabled) {
            return isLg ? <div sx={textStyle}>Stop sharing</div> : <div sx={textStyle}>Stop</div>;
        }
        return isLg ? <div sx={textStyle}>Share screen</div> : <div sx={textStyle}>Screen</div>;
    }

    return (
        <Box flexDirection="row" display="flex">
            <Box onClick={handleClick} sx={effectiveStyle} alignItems="center" justifyContent="center"
                 flexDirection="row">
                <div style={shareScreenBoxStyle}>
                    {<ShareScreenIcon/>}
                </div>
                {buttonText(enabled)}
            </Box>
        </Box>
    )
}

export const NewShareScreenButton = forwardRef(({ onChange, disabled, room }, ref) => {
    const { buttonProps, enabled } = useCustomTrackToggle({
        source: Track.Source.ScreenShare,
        onChange: onChange,
        maybeRoom: room
    });

    const handleClick = () => {
        buttonProps.onClick();
    };

    // Expose imperative handle
    useImperativeHandle(ref, () => ({
        click: handleClick
    }));

    const buttonStyle = {
        display: "flex",
        padding: {md: "4px 4px"},
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: "5px",
        border: "1px solid var(--app-border-subtle)",
        backgroundColor: disabled ? "var(--app-border-subtle)" : "var(--app-bg-subtle)",
        width: "40px",
        height: "40px",
        opacity: disabled ? 0.6 : 1,
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: disabled ? 'var(--app-border-subtle)' : 'var(--app-border-subtle)',
        },
    }

    const enabledButtonStyle = {
        ...buttonStyle,
        backgroundColor: disabled ? "var(--app-border-subtle)" : "var(--app-border-subtle)",
        border: "1.5px solid #2B6BFD"
    };

    const effectiveStyle = enabled ? enabledButtonStyle : buttonStyle;

    const shareScreenBoxStyle = {
        width: "20px",
        height: "15px",
        padding: "1px",
        flexShrink: 0,
        borderRadius: "3px",
        border: "1.5px solid var(--app-text-primary)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    };

    return (
        <Tooltip title={"Share screen"} placement={"bottom"}>
            <Box flexDirection="row" display="flex">
                <Box
                    onClick={handleClick}
                    sx={effectiveStyle}
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="row"
                >
                    <div style={shareScreenBoxStyle}>
                        <ShareScreenIcon />
                    </div>
                </Box>
            </Box>
        </Tooltip>
    );
});

export const MediaButton = ({onClick, mediaBoardOpen}) => {
    const buttonStyle = {
        display: "flex",
        padding: "12px 18px",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        width: "145px",
        height: "40px",
        transition: 'background-color 0.3s',
        backgroundColor: "#2B6BFD",
        '&:hover': {
            background: "linear-gradient(0deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.15) 100%), #2B6BFD", // Darker primary color
        },
        // 5px border radius on the top left and bottom left corners
        borderRadius: "8px",
    }

    const enabledButtonStyle = {
        ...buttonStyle,
        backgroundColor: "#2B6BFD",
        border: "1.5px solid #2B6BFD",
    }

    const mediaBoxStyle = {
        width: "20px",
        height: "15px",
        padding: "1px",
        flexShrink: 0,
        borderRadius: "3px",
        border: "1.5px solid var(--app-text-primary)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    }

    const effectiveStyle = mediaBoardOpen ? enabledButtonStyle : buttonStyle;

    const theme = useTheme();

    const isLg = useMediaQuery(theme.breakpoints.up("lg"));

    const mediaButtonTextStyle = {
        fontFamily: "Inter",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "20px",
        letterSpacing: "0.14px",
        color: "#ffffff",
    }

    return (
        <Box flexDirection="row" display="flex">
            <Box onClick={onClick} sx={effectiveStyle} alignItems="center" justifyContent="center" flexDirection="row">
                <div style={mediaBoxStyle}>
                    {<MusicVideo sx={{color: "#ffffff"}}/>}
                </div>
                <div style={mediaButtonTextStyle}>Mediaboard</div>
            </Box>
        </Box>
    )

}

export const PrejoinMicrophoneButton = ({
                                            onChange,
                                            audioDeviceId,
                                            audioEnabled,
                                            audioTrack,
                                            onActiveDeviceChange,
                                            toggleRef
                                        }) => {
    const {buttonProps, enabled} = useTrackToggle({source: Track.Source.Microphone, onChange: onChange});

    const handleClick = () => {
        toggleRef.current.click();
        buttonProps.onClick();
    }

    const buttonStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        background: "var(--app-bg-subtle)",
        '&:hover': {
            background: "var(--app-border-subtle)",
        },
        backdropFilter: "blur(12.5px)",
        width: "52px",
        height: "52px",
        transition: 'background-color 0.3s',
        borderRadius: "8px 0 0 8px",
    }

    return (
        <Box flexDirection="row" display="flex">
            <Box onClick={handleClick} sx={buttonStyle} alignItems="center" justifyContent="center" flexDirection="row">
                {audioEnabled ? <MicrophoneIcon color={"var(--app-text-primary)"}/> : <MicDisabledIcon color={"var(--app-text-primary)"}/>}
            </Box>
            <NewMediaDeviceMenu kind="audioinput" room={null} initialSelection={audioDeviceId}
                                tracks={{audioinput: audioTrack}} disabled={!audioTrack} isPrejoin={true}
                                onActiveDeviceChange={onActiveDeviceChange}/>
        </Box>
    )
}

export const PrejoinCameraButton = ({
                                        onChange,
                                        videoDeviceId,
                                        videoEnabled,
                                        videoTrack,
                                        onActiveDeviceChange,
                                        toggleRef
                                    }) => {
    const {buttonProps, enabled} = useTrackToggle({
        source: Track.Source.Camera,
        initialState: videoEnabled,
        onChange: onChange
    });

    const handleClick = () => {
        // Click html button ref toggleRef
        toggleRef.current.click();
        buttonProps.onClick();
    }

    const buttonStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "8px 0 0 8px",
        gap: "10px",
        cursor: "pointer",
        background: "var(--app-bg-subtle)",
        '&:hover': {
            background: "var(--app-border-subtle)",
        },
        // backdropFilter: "blur(12.5px)",
        width: "52px",
        height: "52px",
        transition: 'background-color 0.3s',
    }

    return (
        <Box flexDirection="row" display="flex">
            <Box onClick={handleClick} sx={buttonStyle} alignItems="center" justifyContent="center" flexDirection="row">
                {enabled ? <CameraIcon color={"var(--app-text-primary)"}/> : <CamDisabledIcon color={"var(--app-text-primary)"}/>}
            </Box>
            <NewMediaDeviceMenu kind="videoinput" room={null} initialSelection={videoDeviceId}
                                tracks={{videoinput: videoTrack}} disabled={!videoTrack} isPrejoin={true}
                                onActiveDeviceChange={onActiveDeviceChange}/>
        </Box>
    )
}


export const MicrophoneButton = ({onChange, saveAudioInputDevice, room}) => {

    const {buttonProps, enabled} = useCustomTrackToggle({
        source: Track.Source.Microphone,
        onChange: onChange,
        maybeRoom: room
    });

    const handleClick = () => {
        buttonProps.onClick();
    }

    const buttonStyle = {
        display: "flex",
        padding: {md: "10px 14px", lg: "12px 18px", xl: "12px 18px"},
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        backgroundColor: "var(--app-bg-subtle)",
        width: {md: "145px", lg: "157px", xl: "179px"},
        height: {md: "34px", lg: "39px", xl: "45px"},
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: 'var(--app-border-subtle)',
        },
        // 5px border radius on the top left and bottom left corners
        borderRadius: "5px 0 0 5px",
    }

    const theme = useTheme();

    const isLg = useMediaQuery(theme.breakpoints.up("lg"));
    const isXl = useMediaQuery(theme.breakpoints.up("xl"));

    const width = isXl ? 179 : isLg ? 157 : 145;

    return (
        <Box flexDirection="row" display="flex">
            <Box onClick={handleClick} sx={buttonStyle} alignItems="center" justifyContent="center" flexDirection="row">
                {enabled ? <MicrophoneIcon/> : <MicDisabledIcon/>}
                <div sx={textStyle}>Microphone</div>
            </Box>
            <NewMediaDeviceMenu kind="audioinput" room={room}
                                onActiveDeviceChange={(_kind, deviceId) => saveAudioInputDevice(deviceId ?? '')}
                                width={width}/>
        </Box>
    )
}

export const NewMicrophoneButton = ({onChange, selectDisabled, isButtonDisabled, saveAudioInputDevice, room, initialState}) => {

    const {buttonProps, enabled} = useCustomTrackToggle({
        source: Track.Source.Microphone,
        onChange: onChange,
        maybeRoom: room,
        initialState: initialState
    });

    const handleClick = () => {
        if (isButtonDisabled) {
            return;
        }
        buttonProps.onClick();
    }

    const buttonStyle = {
        display: "flex",
        padding: {md: "4px 4px"},
        justifyContent: "center",
        alignItems: "center",
        border: "1px solid var(--app-border-subtle)",
        gap: "10px",
        cursor: isButtonDisabled ? "not-allowed" : "pointer",
        backgroundColor: isButtonDisabled ? "var(--app-border-subtle)" : "var(--app-bg-subtle)",
        width: "40px", // "145px",
        height: "40px",
        opacity: isButtonDisabled ? 0.6 : 1,
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: isButtonDisabled ? 'var(--app-border-subtle)' : 'var(--app-border-subtle)',
        },
        // 5px border radius on the top left and bottom left corners
        borderRadius: "5px 0 0 5px",
    }

    const theme = useTheme();

    const isLg = useMediaQuery(theme.breakpoints.up("lg"));
    const isXl = useMediaQuery(theme.breakpoints.up("xl"));

    const width = isXl ? 179 : isLg ? 157 : 145;

    return (
        <Box flexDirection="row" display="flex">
            <Tooltip title={enabled ? "Mute" : "Unmute"} placement={"bottom"}>
                <Box onClick={handleClick} sx={buttonStyle} alignItems="center" justifyContent="center"
                     flexDirection="row">
                    {enabled ? <MicrophoneIcon/> : <MicDisabledIcon/>}
                </Box>
            </Tooltip>
            <NewMediaDeviceMenu kind="audioinput" room={room} isDisabled={selectDisabled}
                                onActiveDeviceChange={(_kind, deviceId) => saveAudioInputDevice(deviceId ?? '')}
                                width={width} isPrejoin={false}/>
        </Box>
    )
}

// ============================================================================
// NEW SIMPLIFIED CONTROL BAR COMPONENTS
// ============================================================================

// Speaker icon for audio output
const SpeakerIcon = ({color = "var(--app-text-primary)"}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
        </svg>
    );
};

// Shared dropdown menu styles (similar to LocalStudio)
const getDropupMenuProps = () => ({
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
    PaperProps: {
        sx: {
            maxHeight: 400,
            minWidth: 260,
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            border: '1px solid var(--app-border-subtle)',
            mt: -1,
            '& .MuiMenuItem-root': {
                minHeight: 40,
                fontSize: '0.875rem',
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
                '&:hover': {
                    backgroundColor: 'rgba(43,107,253,0.06)',
                },
                '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: 'rgba(43,107,253,0.06)',
                    },
                },
            },
        },
    },
});

// Simplified button style used by Screen Share and standalone buttons
const simpleButtonStyle = (disabled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    height: 34,
    minWidth: 160,
    px: 1.5,
    borderRadius: '6px',
    border: '1px solid var(--app-border-subtle)',
    backgroundColor: disabled ? 'var(--app-bg-subtle)' : 'var(--app-bg-surface)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background-color 0.12s ease, border-color 0.12s ease',
    '&:hover': {
        backgroundColor: 'var(--app-bg-subtle)',
        borderColor: 'var(--app-border-muted)',
    },
});

// Split button: icon toggle on the left, label+arrow on the right.
// Rendered as a single outlined pill; the icon side uses a subtle right separator.
const splitControlMainButtonStyle = (disabled = false, active = false) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 34,
    borderRadius: '6px 0 0 6px',
    border: '1px solid var(--app-border-subtle)',
    borderRight: '1px solid var(--app-border-subtle)',
    backgroundColor: disabled ? 'var(--app-bg-subtle)' : 'var(--app-bg-surface)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background-color 0.12s ease',
    '&:hover': {
        backgroundColor: 'var(--app-bg-subtle)',
    },
});

const splitControlMenuButtonStyle = (disabled = false, open = false) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 0.75,
    minWidth: 124,
    width: 124,
    height: 34,
    px: 1.25,
    borderRadius: '0 6px 6px 0',
    border: '1px solid var(--app-border-subtle)',
    borderLeft: 'none',
    backgroundColor: disabled ? 'var(--app-bg-subtle)' : 'var(--app-bg-surface)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background-color 0.12s ease',
    '&:hover': {
        backgroundColor: 'var(--app-bg-subtle)',
    },
});

const labelStyle = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.825rem',
    fontWeight: 400,
    color: 'var(--app-text-primary)',
    userSelect: 'none',
};

export const RecordingModeReadOnlyButton = ({ recordingMode }) => {
    const modeLabel = recordingMode === 'audio' ? 'Audio only' : 'Video & audio';

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: 156,
                height: 38,
                px: 1.5,
                flexShrink: 0,
                borderRadius: '999px',
                border: '1px solid var(--app-border-subtle)',
                backgroundColor: 'var(--app-bg-subtle)',
                cursor: 'default',
                userSelect: 'none',
            }}
        >
            <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                {recordingMode === 'audio' ? <MicrophoneIcon color="var(--app-text-muted)" /> : <CameraIcon color="var(--app-text-muted)" />}
                <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--app-text-muted)',
                    whiteSpace: 'nowrap',
                }}>
                    {modeLabel}
                </Typography>
            </Box>
            <KeyboardArrowUpIcon sx={{ fontSize: 18, color: 'var(--app-text-subtle)' }} />
        </Box>
    );
};

/**
 * Simplified Screen Share Button - Toggles screen share on/off.
 */
export const SimpleScreenShareButton = forwardRef(
    ({ room, disabled = false, onChange }, ref) => {
        const browserSupportsScreenSharing = typeof navigator !== 'undefined' &&
            !!navigator?.mediaDevices?.getDisplayMedia;

        const isDisabled = disabled || !browserSupportsScreenSharing;

        const { buttonProps, enabled } = useCustomTrackToggle({
            source: Track.Source.ScreenShare,
            onChange,
            maybeRoom: room,
            captureOptions: { audio: true, selfBrowserSurface: 'include' },
        });

        const handleClick = () => {
            if (isDisabled) return;
            buttonProps.onClick();
        };

        useImperativeHandle(ref, () => ({
            click: handleClick,
        }));

        const effectiveStyle = {
            ...simpleButtonStyle(isDisabled),
            ...(enabled ? { backgroundColor: 'var(--app-bg-subtle)' } : null),
        };

        const shareScreenBoxStyle = {
            width: '18px',
            height: '14px',
            flexShrink: 0,
            borderRadius: '3px',
            border: '1.5px solid var(--app-text-primary)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        };

        const tooltip = !browserSupportsScreenSharing
            ? 'Screen sharing not supported'
            : enabled ? 'Stop sharing screen' : 'Share screen';

        return (
            <Tooltip title={tooltip} placement="top">
                <Box onClick={handleClick} sx={effectiveStyle}>
                    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
                        <Box sx={shareScreenBoxStyle}>
                            <ShareScreenIcon />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                ...labelStyle,
                                display: 'inline-flex',
                                alignItems: 'center',
                                lineHeight: 1,
                                transform: 'translateY(0.25px)',
                            }}
                        >
                            Screen
                        </Box>
                    </Box>
                </Box>
            </Tooltip>
        );
    },
);

/**
 * Simplified Audio Button - Shows "Audio" label with icon and arrow.
 * Opens a combined Microphone + Speaker dropdown.
 */
export const SimpleAudioButton = ({
    room,
    onMicToggle,
    isMicEnabled = true,
    selectDisabled = false,
    saveAudioInputDevice,
    saveAudioOutputDevice,
    initialAudioInputDeviceId = '',
    initialAudioOutputDeviceId = '',
    onSpeakerMuteChange,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    const [selectedInputId, setSelectedInputId] = useState(initialAudioInputDeviceId);
    const [selectedOutputId, setSelectedOutputId] = useState(initialAudioOutputDeviceId);
    const [lastActiveInputId, setLastActiveInputId] = useState(
        initialAudioInputDeviceId && initialAudioInputDeviceId !== 'none' ? initialAudioInputDeviceId : ''
    );
    const [micEnabled, setMicEnabled] = useState(() => {
        if (room?.localParticipant) return !!room.localParticipant.isMicrophoneEnabled;
        return isMicEnabled;
    });
    const menuButtonRef = useRef(null);
    const open = Boolean(anchorEl);

    // Initialize with lobby device choices
    useEffect(() => {
        if (initialAudioInputDeviceId && !selectedInputId) {
            setSelectedInputId(initialAudioInputDeviceId);
            if (initialAudioInputDeviceId !== 'none') {
                setLastActiveInputId(initialAudioInputDeviceId);
            }
        }
        if (initialAudioOutputDeviceId && !selectedOutputId) {
            setSelectedOutputId(initialAudioOutputDeviceId);
        }
    }, [initialAudioInputDeviceId, initialAudioOutputDeviceId, selectedInputId, selectedOutputId]);

    useEffect(() => {
        if (!room) {
            setMicEnabled(isMicEnabled);
            return;
        }

        const syncMicState = () => {
            setMicEnabled(!!room.localParticipant?.isMicrophoneEnabled);
        };

        syncMicState();
        room.on?.(RoomEvent.LocalTrackPublished, syncMicState);
        room.on?.(RoomEvent.LocalTrackUnpublished, syncMicState);
        room.on?.(RoomEvent.TrackMuted, syncMicState);
        room.on?.(RoomEvent.TrackUnmuted, syncMicState);

        return () => {
            room.off?.(RoomEvent.LocalTrackPublished, syncMicState);
            room.off?.(RoomEvent.LocalTrackUnpublished, syncMicState);
            room.off?.(RoomEvent.TrackMuted, syncMicState);
            room.off?.(RoomEvent.TrackUnmuted, syncMicState);
        };
    }, [room, isMicEnabled]);

    // Enumerate devices
    useEffect(() => {
        const enumerate = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioInputs = devices.filter((d) => d.kind === 'audioinput');
                const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');
                setAudioInputDevices(audioInputs);
                setAudioOutputDevices(audioOutputs);
                setSelectedInputId((previous) => previous || audioInputs[0]?.deviceId || '');
                setLastActiveInputId((previous) => previous || audioInputs[0]?.deviceId || '');
                setSelectedOutputId((previous) => previous || audioOutputs[0]?.deviceId || '');
            } catch (err) {
                console.error('Error enumerating audio devices:', err);
            }
        };
        enumerate();
        navigator.mediaDevices.addEventListener('devicechange', enumerate);
        return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate);
    }, []);

    const handleArrowClick = (e) => {
        if (selectDisabled) return;
        e.stopPropagation();
        setAnchorEl(anchorEl ? null : menuButtonRef.current);
    };

    const handleClose = () => setAnchorEl(null);

    const handleMicSelect = async (deviceId) => {
        setSelectedInputId(deviceId);
        if (deviceId === 'none') {
            // Mute microphone
            if (room?.localParticipant) {
                await room.localParticipant.setMicrophoneEnabled(false);
            }
            setMicEnabled(false);
            onMicToggle?.(false);
        } else {
            setLastActiveInputId(deviceId);
            saveAudioInputDevice?.(deviceId);
            if (!room?.localParticipant) return;

            try {
                await room.switchActiveDevice('audioinput', deviceId);
            } catch {}

            const pub = room.localParticipant.getTrackPublication?.(Track.Source.Microphone);
            const track = pub?.audioTrack;
            const isPlaceholder = !!track?.isUserProvided;
            let didRestart = false;

            if (track && isPlaceholder) {
                try {
                    const defaults = room.options?.audioCaptureDefaults ?? {};
                    await track.restartTrack?.({ ...defaults, deviceId: { exact: deviceId } });
                    didRestart = true;
                } catch {}
            }

            // Enable mic (unmutes if already published)
            try {
                await room.localParticipant.setMicrophoneEnabled(true);
            } catch {}

            if (track && isPlaceholder && didRestart) {
                // Switch the track back to SDK-managed semantics after activation (stop on mute, etc.)
                try {
                    await track.replaceTrack?.(track.mediaStreamTrack, { userProvidedTrack: false });
                } catch {}
                releasePlaceholderResources(room, Track.Source.Microphone);
            }
            setMicEnabled(true);
            onMicToggle?.(true);
        }
    };

    const handleMicToggle = async (e) => {
        e.stopPropagation();
        if (selectDisabled) return;

        if (!room?.localParticipant) {
            const nextState = !micEnabled;
            setMicEnabled(nextState);
            onMicToggle?.(nextState);
            return;
        }

        if (micEnabled) {
            try {
                await room.localParticipant.setMicrophoneEnabled(false);
            } catch {}
            setSelectedInputId('none');
            setMicEnabled(false);
            onMicToggle?.(false);
            return;
        }

        const fallbackInputId = selectedInputId && selectedInputId !== 'none'
            ? selectedInputId
            : (lastActiveInputId || audioInputDevices[0]?.deviceId || '');

        if (fallbackInputId) {
            await handleMicSelect(fallbackInputId);
            return;
        }

        try {
            await room.localParticipant.setMicrophoneEnabled(true);
        } catch {}
        setMicEnabled(true);
        onMicToggle?.(true);
    };

    const handleSpeakerSelect = (deviceId) => {
        setSelectedOutputId(deviceId);
        if (deviceId === 'none') {
            // Mute all remote audio tracks
            if (room) {
                room.remoteParticipants.forEach((participant) => {
                    participant.audioTrackPublications.forEach((publication) => {
                        if (publication.track) {
                            publication.track.setVolume(0);
                        }
                    });
                });
            }
            onSpeakerMuteChange?.(true);
            saveAudioOutputDevice?.(deviceId);
        } else {
            // Unmute all remote audio tracks
            if (room) {
                room.remoteParticipants.forEach((participant) => {
                    participant.audioTrackPublications.forEach((publication) => {
                        if (publication.track) {
                            publication.track.setVolume(1);
                        }
                    });
                });
                room.switchActiveDevice('audiooutput', deviceId);
            }
            onSpeakerMuteChange?.(false);
            saveAudioOutputDevice?.(deviceId);
        }
    };

    const selectedInput = audioInputDevices.find((d) => d.deviceId === selectedInputId);
    const audioDeviceLabel = selectedInputId === 'none'
        ? 'No microphone'
        : (selectedInput?.label || (selectedInputId ? 'Microphone' : 'Microphone'));

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box sx={{ position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={micEnabled ? 'Mute microphone' : 'Unmute microphone'} placement="top">
                        <Box onClick={handleMicToggle} sx={splitControlMainButtonStyle(selectDisabled, micEnabled)}>
                            {micEnabled ? <MicrophoneIcon /> : <MicDisabledIcon color="var(--app-text-muted)" />}
                        </Box>
                    </Tooltip>
                    <Tooltip title="Audio devices" placement="top">
                        <Box
                            ref={menuButtonRef}
                            onClick={handleArrowClick}
                            sx={splitControlMenuButtonStyle(selectDisabled, open)}
                        >
                            <Box
                                component="span"
                                sx={{
                                    ...labelStyle,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {audioDeviceLabel}
                            </Box>
                            <KeyboardArrowUpIcon sx={{
                                fontSize: 18,
                                color: 'var(--app-text-subtle)',
                                marginLeft: 'auto',
                                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            }} />
                        </Box>
                    </Tooltip>
                </Box>

                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="top-start"
                    style={{ zIndex: 1400 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                >
                    <Box sx={{
                        backgroundColor: 'var(--app-bg-surface)',
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        border: '1px solid var(--app-border-subtle)',
                        minWidth: 260,
                        py: 1,
                        overflow: 'hidden',
                    }}>
                        {/* Microphone Section */}
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'var(--app-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            px: 2,
                            py: 1,
                        }}>
                            Microphone
                        </Typography>
                        {audioInputDevices.map((device) => (
                            <Box
                                key={device.deviceId}
                                onClick={() => handleMicSelect(device.deviceId)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1,
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                                }}
                            >
                                <Typography sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '13px',
                                    color: 'var(--app-text-primary)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 200,
                                }}>
                                    {device.label || `Microphone ${audioInputDevices.indexOf(device) + 1}`}
                                </Typography>
                                {selectedInputId === device.deviceId && (
                                    <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                )}
                            </Box>
                        ))}
                        {/* No Microphone option */}
                        <Box
                            onClick={() => handleMicSelect('none')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                            }}
                        >
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '13px',
                                color: 'var(--app-text-muted)',
                                fontStyle: 'italic',
                            }}>
                                No Microphone
                            </Typography>
                            {selectedInputId === 'none' && (
                                <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                            )}
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        {/* Speakers Section */}
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'var(--app-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            px: 2,
                            py: 1,
                        }}>
                            Speakers
                        </Typography>
                        {audioOutputDevices.length === 0 ? (
                            <Box sx={{ px: 2, py: 1 }}>
                                <Typography sx={{ fontSize: '13px', color: 'var(--app-text-subtle)' }}>
                                    System Default
                                </Typography>
                            </Box>
                        ) : (
                            audioOutputDevices.map((device) => (
                                <Box
                                    key={device.deviceId}
                                    onClick={() => handleSpeakerSelect(device.deviceId)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: 2,
                                        py: 1,
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                                    }}
                                >
                                    <Typography sx={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: '13px',
                                        color: 'var(--app-text-primary)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 200,
                                    }}>
                                        {device.label || `Speaker ${audioOutputDevices.indexOf(device) + 1}`}
                                    </Typography>
                                    {selectedOutputId === device.deviceId && (
                                        <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                    )}
                                </Box>
                            ))
                        )}
                        {/* No Speaker option */}
                        <Box
                            onClick={() => handleSpeakerSelect('none')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                            }}
                        >
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '13px',
                                color: 'var(--app-text-muted)',
                                fontStyle: 'italic',
                            }}>
                                No Speaker
                            </Typography>
                            {selectedOutputId === 'none' && (
                                <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                            )}
                        </Box>
                    </Box>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

// Resolution options matching LocalStudio
const RESOLUTION_OPTIONS = [
    { label: '480p', width: 854, height: 480 },
    { label: '720p', width: 1280, height: 720 },
    { label: '1080p', width: 1920, height: 1080 },
    { label: '1440p', width: 2560, height: 1440 },
    { label: '4K', width: 3840, height: 2160 },
];

/**
 * Simplified Video Button - Shows "Video" label with icon and arrow.
 * Opens a camera + resolution dropdown.
 */
export const SimpleVideoButton = ({
    room,
    onCamToggle,
    isCamEnabled = true,
    selectDisabled = false,
    saveVideoInputDevice,
    initialVideoInputDeviceId = '',
    selectedResolution = '1080p',
    onResolutionChange,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedId, setSelectedId] = useState(initialVideoInputDeviceId);
    const [lastActiveVideoId, setLastActiveVideoId] = useState(
        initialVideoInputDeviceId && initialVideoInputDeviceId !== 'none' ? initialVideoInputDeviceId : ''
    );
    const [resolution, setResolution] = useState(selectedResolution);
    const [cameraEnabled, setCameraEnabled] = useState(() => {
        if (room?.localParticipant) return !!room.localParticipant.isCameraEnabled;
        return isCamEnabled;
    });
    const menuButtonRef = useRef(null);
    const open = Boolean(anchorEl);

    // Initialize with lobby device choice
    useEffect(() => {
        if (initialVideoInputDeviceId && !selectedId) {
            setSelectedId(initialVideoInputDeviceId);
            if (initialVideoInputDeviceId !== 'none') {
                setLastActiveVideoId(initialVideoInputDeviceId);
            }
        }
    }, [initialVideoInputDeviceId, selectedId]);

    useEffect(() => {
        if (!room) {
            setCameraEnabled(isCamEnabled);
            return;
        }

        const syncCameraState = () => {
            setCameraEnabled(!!room.localParticipant?.isCameraEnabled);
        };

        syncCameraState();
        room.on?.(RoomEvent.LocalTrackPublished, syncCameraState);
        room.on?.(RoomEvent.LocalTrackUnpublished, syncCameraState);
        room.on?.(RoomEvent.TrackMuted, syncCameraState);
        room.on?.(RoomEvent.TrackUnmuted, syncCameraState);

        return () => {
            room.off?.(RoomEvent.LocalTrackPublished, syncCameraState);
            room.off?.(RoomEvent.LocalTrackUnpublished, syncCameraState);
            room.off?.(RoomEvent.TrackMuted, syncCameraState);
            room.off?.(RoomEvent.TrackUnmuted, syncCameraState);
        };
    }, [room, isCamEnabled]);

    // Sync resolution prop
    useEffect(() => {
        if (selectedResolution) {
            setResolution(selectedResolution);
        }
    }, [selectedResolution]);

    // Enumerate devices
    useEffect(() => {
        const enumerate = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videos = devices.filter((d) => d.kind === 'videoinput');
                setVideoDevices(videos);
                setSelectedId((previous) => previous || videos[0]?.deviceId || '');
                setLastActiveVideoId((previous) => previous || videos[0]?.deviceId || '');
            } catch (err) {
                console.error('Error enumerating video devices:', err);
            }
        };
        enumerate();
        navigator.mediaDevices.addEventListener('devicechange', enumerate);
        return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate);
    }, []);

    const handleArrowClick = (e) => {
        if (selectDisabled) return;
        e.stopPropagation();
        setAnchorEl(anchorEl ? null : menuButtonRef.current);
    };

    const handleClose = () => setAnchorEl(null);

    const handleSelect = async (deviceId) => {
        setSelectedId(deviceId);
        if (deviceId === 'none') {
            // Disable camera
            if (room?.localParticipant) {
                await room.localParticipant.setCameraEnabled(false);
            }
            setCameraEnabled(false);
            onCamToggle?.(false);
        } else {
            setLastActiveVideoId(deviceId);
            saveVideoInputDevice?.(deviceId);
            if (!room?.localParticipant) return;

            try {
                await room.switchActiveDevice('videoinput', deviceId);
            } catch {}

            const pub = room.localParticipant.getTrackPublication?.(Track.Source.Camera);
            const track = pub?.videoTrack;
            const isPlaceholder = !!track?.isUserProvided;
            let didRestart = false;

            if (track && isPlaceholder) {
                try {
                    const defaults = room.options?.videoCaptureDefaults ?? {};
                    await track.restartTrack?.({ ...defaults, deviceId: { exact: deviceId } });
                    didRestart = true;
                } catch {}
            }

            // Enable camera (unmutes if already published)
            try {
                await room.localParticipant.setCameraEnabled(true);
            } catch {}

            if (track && isPlaceholder && didRestart) {
                // Switch the track back to SDK-managed semantics after activation (stop on mute, etc.)
                try {
                    await track.replaceTrack?.(track.mediaStreamTrack, { userProvidedTrack: false });
                } catch {}
                releasePlaceholderResources(room, Track.Source.Camera);
            }
            setCameraEnabled(true);
            onCamToggle?.(true);
        }
    };

    const handleCameraToggle = async (e) => {
        e.stopPropagation();
        if (selectDisabled) return;

        if (!room?.localParticipant) {
            const nextState = !cameraEnabled;
            setCameraEnabled(nextState);
            onCamToggle?.(nextState);
            return;
        }

        if (cameraEnabled) {
            try {
                await room.localParticipant.setCameraEnabled(false);
            } catch {}
            setSelectedId('none');
            setCameraEnabled(false);
            onCamToggle?.(false);
            return;
        }

        const fallbackVideoId = selectedId && selectedId !== 'none'
            ? selectedId
            : (lastActiveVideoId || videoDevices[0]?.deviceId || '');

        if (fallbackVideoId) {
            await handleSelect(fallbackVideoId);
            return;
        }

        try {
            await room.localParticipant.setCameraEnabled(true);
        } catch {}
        setCameraEnabled(true);
        onCamToggle?.(true);
    };

    const handleResolutionSelect = (res) => {
        setResolution(res);
        onResolutionChange?.(res);
    };

    const selectedVideo = videoDevices.find((d) => d.deviceId === selectedId);
    const videoDeviceLabel = selectedId === 'none'
        ? 'No camera'
        : (selectedVideo?.label || (selectedId ? 'Camera' : 'Camera'));

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box sx={{ position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={cameraEnabled ? 'Disable camera' : 'Enable camera'} placement="top">
                        <Box onClick={handleCameraToggle} sx={splitControlMainButtonStyle(selectDisabled, cameraEnabled)}>
                            {cameraEnabled ? <CameraIcon /> : <CamDisabledIcon color="var(--app-text-muted)" />}
                        </Box>
                    </Tooltip>
                    <Tooltip title="Camera and resolution" placement="top">
                        <Box
                            ref={menuButtonRef}
                            onClick={handleArrowClick}
                            sx={splitControlMenuButtonStyle(selectDisabled, open)}
                        >
                            <Box
                                component="span"
                                sx={{
                                    ...labelStyle,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {videoDeviceLabel}
                            </Box>
                            <KeyboardArrowUpIcon sx={{
                                fontSize: 18,
                                color: 'var(--app-text-subtle)',
                                marginLeft: 'auto',
                                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            }} />
                        </Box>
                    </Tooltip>
                </Box>

                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="top-start"
                    style={{ zIndex: 1400 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                >
                    <Box sx={{
                        backgroundColor: 'var(--app-bg-surface)',
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        border: '1px solid var(--app-border-subtle)',
                        minWidth: 260,
                        py: 1,
                        overflow: 'hidden',
                        maxHeight: 400,
                        overflowY: 'auto',
                    }}>
                        {/* Camera Section */}
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'var(--app-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            px: 2,
                            py: 1,
                        }}>
                            Camera
                        </Typography>
                        {videoDevices.map((device) => (
                            <Box
                                key={device.deviceId}
                                onClick={() => handleSelect(device.deviceId)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1,
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                                }}
                            >
                                <Typography sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '13px',
                                    color: 'var(--app-text-primary)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 200,
                                }}>
                                    {device.label || `Camera ${videoDevices.indexOf(device) + 1}`}
                                </Typography>
                                {selectedId === device.deviceId && (
                                    <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                )}
                            </Box>
                        ))}
                        {/* No Camera option */}
                        <Box
                            onClick={() => handleSelect('none')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                            }}
                        >
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '13px',
                                color: 'var(--app-text-muted)',
                                fontStyle: 'italic',
                            }}>
                                No Camera
                            </Typography>
                            {selectedId === 'none' && (
                                <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                            )}
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        {/* Resolution Section */}
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'var(--app-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            px: 2,
                            py: 1,
                        }}>
                            Resolution
                        </Typography>
                        {RESOLUTION_OPTIONS.map((opt) => (
                            <Box
                                key={opt.label}
                                onClick={() => handleResolutionSelect(opt.label)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1,
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                                }}
                            >
                                <Typography sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '13px',
                                    color: 'var(--app-text-primary)',
                                }}>
                                    {opt.label}
                                    <Typography component="span" sx={{
                                        fontSize: '11px',
                                        color: 'var(--app-text-subtle)',
                                        ml: 1,
                                    }}>
                                        ({opt.width}×{opt.height})
                                    </Typography>
                                </Typography>
                                {resolution === opt.label && (
                                    <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

/**
 * Record Button with Recording Mode Selector
 * Shows a dedicated mode selector button beside the primary record button.
 */
export const RecordButtonWithModeSelector = ({
    recordingInProgress,
    localRecordingInProgress,
    onStartRecording,
    onStopRecording,
    recordingMode, // 'audio' or 'video'
    onRecordingModeChange,
    disabled = false,
}) => {
    const [modeAnchorEl, setModeAnchorEl] = useState(null);
    const [recordTime, setRecordTime] = useState(0);
    const timerRef = useRef(null);
    const startedAtRef = useRef(null);
    const buttonRef = useRef(null);
    const modeOpen = Boolean(modeAnchorEl);

    // Recording timer
    useEffect(() => {
        if (recordingInProgress) {
            const startedAt = Date.now();
            startedAtRef.current = startedAt;

            const tick = () => {
                setRecordTime(Math.floor((Date.now() - startedAt) / 1000));
            };

            tick();
            timerRef.current = setInterval(tick, 1000);
        } else {
            clearInterval(timerRef.current);
            timerRef.current = null;
            startedAtRef.current = null;
            setRecordTime(0);
        }
        return () => {
            clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [recordingInProgress]);

    const formatTime = () => {
        const minutes = Math.floor(recordTime / 60).toString().padStart(2, '0');
        const seconds = (recordTime % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const handleRecordClick = async () => {
        if (disabled || (localRecordingInProgress && !recordingInProgress)) return;
        if (recordingInProgress) {
            await onStopRecording?.();
        } else {
            await onStartRecording?.();
        }
    };

    const handleModeButtonClick = (e) => {
        e.stopPropagation();
        if (recordingInProgress || disabled) return;
        setModeAnchorEl(modeAnchorEl ? null : buttonRef.current);
    };

    const handleModeSelect = (mode) => {
        onRecordingModeChange?.(mode);
        setModeAnchorEl(null);
    };

    const isStarting = localRecordingInProgress && !recordingInProgress;
    const modeLabel = recordingMode === 'audio' ? 'Audio only' : 'Video & audio';

    return (
        <ClickAwayListener onClickAway={() => setModeAnchorEl(null)}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    ref={buttonRef}
                    onClick={handleModeButtonClick}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        width: 156,
                        height: 38,
                        px: 1.5,
                        flexShrink: 0,
                        borderRadius: '999px',
                        border: '1px solid var(--app-border-subtle)',
                        backgroundColor: disabled || recordingInProgress ? 'var(--app-bg-subtle)' : 'var(--app-bg-surface)',
                        cursor: disabled || recordingInProgress ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.65 : 1,
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                        '&:hover': {
                            backgroundColor: disabled || recordingInProgress ? 'var(--app-bg-subtle)' : 'var(--app-bg-subtle)',
                            borderColor: disabled || recordingInProgress ? 'var(--app-border-subtle)' : 'var(--app-border-muted)',
                        },
                    }}
                >
                    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                        {recordingMode === 'audio' ? <MicrophoneIcon color="var(--app-icon-primary)" /> : <CameraIcon color="var(--app-icon-primary)" />}
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'var(--app-text-primary)',
                            whiteSpace: 'nowrap',
                        }}>
                            {modeLabel}
                        </Typography>
                    </Box>
                    <KeyboardArrowUpIcon sx={{
                        fontSize: 18,
                        color: 'var(--app-text-subtle)',
                        transform: modeOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                    }} />
                </Box>

                <Box>
                    <Box
                        onClick={handleRecordClick}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.75,
                            minWidth: 124,
                            height: 40,
                            px: 2.25,
                            backgroundColor: recordingInProgress ? '#1A1A1A' : '#E53935',
                            borderRadius: '999px',
                            cursor: disabled || isStarting ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.6 : 1,
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                                backgroundColor: disabled || isStarting ? undefined : (recordingInProgress ? '#333' : '#C62828'),
                            },
                        }}
                    >
                        {recordingInProgress ? (
                            <>
                                <Box sx={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: 'white',
                                    borderRadius: 0.5,
                                    animation: 'stopFlash 1s ease-in-out infinite',
                                    '@keyframes stopFlash': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.4 },
                                    },
                                }} />
                                <Typography sx={{
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    fontFamily: 'Inter, sans-serif',
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {formatTime()}
                                </Typography>
                            </>
                        ) : isStarting ? (
                            <Typography sx={{
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Inter, sans-serif',
                            }}>
                                Starting...
                            </Typography>
                        ) : (
                            <>
                                <FiberManualRecordIcon sx={{ color: 'white', fontSize: 16 }} />
                                <Typography sx={{
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    fontFamily: 'Inter, sans-serif',
                                }}>
                                    Record
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Mode Selector Popover */}
                <Popper
                    open={modeOpen}
                    anchorEl={modeAnchorEl}
                    placement="top"
                    style={{ zIndex: 1400 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
                >
                    <Box sx={{
                        backgroundColor: 'var(--app-bg-surface)',
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        border: '1px solid var(--app-border-subtle)',
                        minWidth: 160,
                        py: 0.5,
                        overflow: 'hidden',
                    }}>
                        <Box
                            onClick={() => handleModeSelect('audio')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1.25,
                                cursor: 'pointer',
                                backgroundColor: recordingMode === 'audio' ? 'rgba(43,107,253,0.06)' : 'transparent',
                                '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                            }}
                        >
                            <MicrophoneIcon color="var(--app-text-primary)" />
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '13px',
                                color: 'var(--app-text-primary)',
                                flex: 1,
                            }}>
                                Audio only
                            </Typography>
                            {recordingMode === 'audio' && (
                                <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)' }} />
                            )}
                        </Box>
                        <Box
                            onClick={() => handleModeSelect('video')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1.25,
                                cursor: 'pointer',
                                backgroundColor: recordingMode === 'video' ? 'rgba(43,107,253,0.06)' : 'transparent',
                                '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                            }}
                        >
                            <CameraIcon color="var(--app-text-primary)" />
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '13px',
                                color: 'var(--app-text-primary)',
                                flex: 1,
                            }}>
                                Video & audio
                            </Typography>
                            {recordingMode === 'video' && (
                                <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)' }} />
                            )}
                        </Box>
                    </Box>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

/**
 * Hangup/Leave Button - Phone icon with slash
 */
export const LeaveCallButton = ({ onClick, disabled = false }) => {
    return (
        <Tooltip title="Leave studio" placement="top">
            <Box
                onClick={disabled ? undefined : onClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 34,
                    height: 34,
                    borderRadius: '6px',
                    backgroundColor: 'var(--app-bg-surface)',
                    border: '1px solid var(--app-border-subtle)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    transition: 'background-color 0.12s ease, border-color 0.12s ease',
                    '&:hover': {
                        backgroundColor: disabled ? undefined : 'var(--app-bg-subtle)',
                        borderColor: disabled ? undefined : 'var(--app-border-muted)',
                    },
                }}
            >
                <CallEndIcon sx={{ fontSize: 20, color: 'var(--app-text-muted)' }} />
            </Box>
        </Tooltip>
    );
};
